import  { useState, useEffect, useCallback } from 'react';
import { 
  Container, Row, Col, 
  Form, Button, Card, 
  Alert, InputGroup, Spinner,
  Table, Badge, Modal,
  Pagination
} from 'react-bootstrap';
import showToast from '../../../utils/Toast';
import { 
  FaEye, FaSearch, FaFilter, FaUsers, FaPhone, 
  FaCalendarAlt, FaBuilding, FaEnvelope, FaClock, FaCheck, FaTimes,
   FaUser, FaCalendarDay, FaTrash,
  FaUtensils, FaGlassCheers, FaInfoCircle, 
  FaUserFriends, FaList, FaIdCard, FaVenusMars, FaMapMarkerAlt,
  FaRegCommentDots, FaStar, FaMoneyBill, FaTag
} from 'react-icons/fa';
import Layout from '../Dashbaord/Layout.js/Layout';


import 'react-toastify/dist/ReactToastify.css';
import { 
bookingService
} from '../../../services/booking.services';
import { colors } from '../../../utils/Colors';
import { useAppData } from '../../../context';
const Bookings = () => {

  const {fetchCounts , newBookingsCount} = useAppData()

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [filters, setFilters] = useState({
    isUnread: 'all',
    time: 'all',
    startDate: '',
    endDate: ''
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 1
  });

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...filters
      };
      
      Object.keys(params).forEach(key => {
        if (params[key] === 'all' || params[key] === '') {
          delete params[key];
        }
      });
      
      const response = await bookingService.getAllBook(params);
      
      setBookings(response.data || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
        pages: response.pages || 1
      }));
      
    } catch (err) {
      setError(err.message || 'Failed to fetch bookings');
      showToast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, filters]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchBookings();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchBookings();
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilters({
      isUnread: 'all',
      time: 'all',
      startDate: '',
      endDate: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchBookings();
  };

  const handleViewBooking = async (id) => {
    setModalLoading(true);
    try {
      const booking = await bookingService.getById(id);
      setSelectedBooking(booking.data);
      setShowViewModal(true);
  
      showToast.success('Booking details loaded');
      fetchCounts()
    } catch (err) {
      showToast.error('Failed to fetch booking details');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteBooking = async (id) => {
    try {
      await bookingService.deleteById(id);
      showToast.success('Booking deleted successfully');
      setShowDeleteModal(false);
      fetchBookings();
      fetchCounts()
    } catch (err) {
      showToast.error('Failed to delete booking');
    }
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
    fetchBookings();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (isUnread) => {
    return isUnread ? (
      <Badge bg="danger" className="d-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: '0.8rem', width: 'fit-content' }}>
        <FaClock size={10} /> New
      </Badge>
    ) : (
      <Badge bg="success" className="d-flex align-items-center gap-1 px-2 py-1" style={{ fontSize: '0.8rem', width: 'fit-content' }}>
        <FaCheck size={10} /> Viewed
      </Badge>
    );
  };

  const timeOptions = [
    { value: 'all', label: 'All Times' },
    { value: 'breakfast', label: 'Breakfast' },
    { value: 'lunch', label: 'Lunch' },
    { value: 'dinner', label: 'Dinner' }
  ];

  const calculateStats = () => {
    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    return {
      total: bookings.length,
      new: bookings.filter(b => b.isUnread).length,
      thisMonth: bookings.filter(b => {
        const bookingDate = new Date(b.createdAt);
        return bookingDate.getMonth() === now.getMonth() && 
               bookingDate.getFullYear() === now.getFullYear();
      }).length,
      today: bookings.filter(b => {
        const bookingDate = new Date(b.functionDate || b.createdAt);
        return bookingDate >= today && bookingDate < tomorrow;
      }).length
    };
  };

  const stats = calculateStats();

  const getTimeSlotLabel = (time) => {
    switch(time) {
      case 'breakfast': return 'Breakfast';
      case 'lunch': return 'Lunch';
      case 'dinner': return 'Dinner';
      default: return time || 'Not specified';
    }
  };

  const parseMenuData = (menu) => {
    if (!menu) return [];
    
    if (typeof menu === 'string') {
      return [{ title: 'Menu', items: menu }];
    }
    
    if (typeof menu === 'object') {
      return Object.entries(menu).map(([key, value]) => ({
        title: key.charAt(0).toUpperCase() + key.slice(1),
        items: value
      }));
    }
    
    return [];
  };

  return (
    <Layout>
      <Container fluid className="py-4">
       
        <Row className="mb-4 align-items-center p-4 rounded-3" style={{backgroundColor: colors.TABLE_HEADERS}}>
          <Col md={6}>
            <div>
              <h2 className="fw-bold mb-2 text-white" style={{ fontSize: '1.75rem' }}>
                <FaCalendarAlt className="me-2" />
                Bookings Management
              </h2>
              <p className="text-white-50 mb-0" style={{ fontSize: '0.9rem' }}>
                <FaUsers className="me-2" />
                Manage all booking records and information
              </p>
            </div>
          </Col>
          
          
        </Row>

        {/* Stats Cards */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>Total Bookings</h6>
                    <h2 className="mb-0" style={{ fontSize: '2rem' }}>{stats.total}</h2>
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>All bookings</small>
                  </div>
                  <div className="bg-primary bg-opacity-10 text-primary rounded-circle p-3">
                    <FaUsers size={20} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>New Bookings</h6>
                    <h2 className="mb-0" style={{ fontSize: '2rem' }}>{newBookingsCount}</h2>
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>Unread bookings</small>
                  </div>
                  <div className="bg-danger bg-opacity-10 text-danger rounded-circle p-3">
                    <FaClock size={20} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>This Month</h6>
                    <h2 className="mb-0" style={{ fontSize: '2rem' }}>{stats.thisMonth}</h2>
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>Current month</small>
                  </div>
                  <div className="bg-success bg-opacity-10 text-success rounded-circle p-3">
                    <FaCalendarAlt size={20} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          <Col md={3}>
            <Card className="border-0 shadow-sm h-100">
              <Card.Body className="p-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h6 className="text-muted mb-1" style={{ fontSize: '0.85rem' }}>Today</h6>
                    <h2 className="mb-0" style={{ fontSize: '2rem' }}>{stats.today}</h2>
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>Today's bookings</small>
                  </div>
                  <div className="bg-warning bg-opacity-10 text-warning rounded-circle p-3">
                    <FaBuilding size={20} />
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Search and Filters */}
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body className="p-3">
            <Row className="g-3">
              <Col md={6}>
                <Form onSubmit={handleSearch}>
                  <InputGroup>
                    <InputGroup.Text className="bg-light" style={{ fontSize: '0.875rem' }}>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search by name, email, phone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="py-2"
                      style={{ fontSize: '0.875rem' }}
                    />
                    <Button type="submit" variant="primary" className="px-4 py-2" style={{ fontSize: '0.875rem' }}>
                      Search
                    </Button>
                  </InputGroup>
                </Form>
              </Col>
              
              <Col md={6}>
                <div className="d-flex gap-2 justify-content-end">
                  <Button
                    variant="outline-secondary"
                    onClick={() => {
                      const collapse = document.getElementById('filterCollapse');
                      if (collapse) {
                        collapse.classList.toggle('show');
                      }
                    }}
                    className="d-flex align-items-center py-2"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <FaFilter className="me-2" />
                    Filters
                  </Button>
                  
                  <Button
                    variant="outline-danger"
                    onClick={resetFilters}
                    className="d-flex align-items-center py-2"
                    style={{ fontSize: '0.875rem' }}
                  >
                    <FaTimes className="me-2" />
                    Clear All
                  </Button>
                </div>
              </Col>
            </Row>
            
            {/* Advanced Filters */}
            <div className="collapse mt-3" id="filterCollapse">
              <Row className="g-3">
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-medium" style={{ fontSize: '0.85rem' }}>Status</Form.Label>
                    <Form.Select
                      value={filters.isUnread}
                      onChange={(e) => handleFilterChange('isUnread', e.target.value)}
                      className="py-2"
                      style={{ fontSize: '0.875rem' }}
                    >
                      <option value="all">All Status</option>
                      <option value="true">New (Unread)</option>
                      <option value="false">Viewed (Read)</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-medium" style={{ fontSize: '0.85rem' }}>Time Slot</Form.Label>
                    <Form.Select
                      value={filters.time}
                      onChange={(e) => handleFilterChange('time', e.target.value)}
                      className="py-2"
                      style={{ fontSize: '0.875rem' }}
                    >
                      {timeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-medium" style={{ fontSize: '0.85rem' }}>From Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={filters.startDate}
                      onChange={(e) => handleFilterChange('startDate', e.target.value)}
                      className="py-2"
                      style={{ fontSize: '0.875rem' }}
                    />
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group>
                    <Form.Label className="fw-medium" style={{ fontSize: '0.85rem' }}>To Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={filters.endDate}
                      onChange={(e) => handleFilterChange('endDate', e.target.value)}
                      className="py-2"
                      style={{ fontSize: '0.875rem' }}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              <div className="mt-3 text-end">
                <Button variant="primary" onClick={applyFilters} className="px-4 py-2" style={{ fontSize: '0.875rem' }}>
                  Apply Filters
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Main Content Table */}
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-3">
            {error && (
              <Alert variant="danger" className="mb-4" style={{ fontSize: '0.875rem' }}>
                <FaInfoCircle className="me-2" />
                {error}
              </Alert>
            )}
            
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2" style={{ fontSize: '0.875rem' }}>Loading bookings...</p>
              </div>
            ) : bookings.length === 0 ? (
              <div className="text-center py-5">
                <FaCalendarAlt size={48} className="text-muted mb-3" />
                <h4 style={{ fontSize: '1.25rem' }}>No bookings found</h4>
                <p className="text-muted" style={{ fontSize: '0.875rem' }}>
                  {searchTerm || Object.values(filters).some(v => v && v !== 'all')
                    ? 'Try changing your search criteria'
                    : 'No bookings have been created yet'}
                </p>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <Table hover className="align-middle" style={{ fontSize: '0.85rem' }}>
                    <thead>
                      <tr className="bg-light">
                        <th style={{ fontSize: '0.9rem' }}>Guest Details</th>
                        <th style={{ fontSize: '0.9rem' }}>Event Information</th>
                        <th style={{ fontSize: '0.9rem' }}>Schedule</th>
                        <th style={{ fontSize: '0.9rem' }}>Status</th>
                        <th className="text-end" style={{ fontSize: '0.9rem' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => (
                        <tr key={booking._id} className={booking.isUnread ? 'table-warning' : ''}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="bg-primary text-white rounded-circle p-2 me-3" style={{ width: '36px', height: '36px' }}>
                                <FaUser size={14} />
                              </div>
                              <div>
                                <strong className="d-block" style={{ fontSize: '0.9rem' }}>{booking.name || 'N/A'}</strong>
                                <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>
                                  <FaEnvelope className="me-1" size={10} />
                                  {booking.email || 'N/A'}
                                </small>
                                {booking.phoneNo && (
                                  <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>
                                    <FaPhone className="me-1" size={10} />
                                    {booking.phoneNo}
                                  </small>
                                )}
                              </div>
                            </div>
                          </td>
                          
                          <td>
                            <div>
                              <div className="d-flex align-items-center mb-1">
                                <FaUserFriends className="me-2 text-primary" size={12} />
                                <strong style={{ fontSize: '0.9rem' }}>Guests: {booking.guestCount || 0}</strong>
                              </div>
                              {booking.menu && (
                                <small className="text-muted d-block" style={{ fontSize: '0.8rem' }}>
                                  <FaList className="me-1" size={10} />
                                  Menu selected
                                </small>
                              )}
                            </div>
                          </td>
                          
                          <td>
                            <div>
                              <div className="d-flex align-items-center mb-1">
                                <FaCalendarAlt className="me-2 text-primary" size={12} />
                                <span style={{ fontSize: '0.9rem' }}>{formatDate(booking.functionDate)}</span>
                              </div>
                              <Badge bg="info" className="d-flex align-items-center gap-1" style={{ width: 'fit-content', fontSize: '0.75rem', padding: '4px 8px' }}>
                                {getTimeSlotLabel(booking.time)}
                              </Badge>
                            </div>
                          </td>
                          
                          <td>
                            {getStatusBadge(booking.isUnread)}
                          </td>
                          
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => handleViewBooking(booking._id)}
                                title="View Details"
                                style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                              >
                                <FaEye size={12} />
                              </Button>
                              
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => {
                                  setSelectedBooking(booking);
                                  setShowDeleteModal(true);
                                }}
                                title="Delete Booking"
                                style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                              >
                                <FaTrash size={12} />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                
                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4">
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                      Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                      {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                      {pagination.total} entries
                    </div>
                    
                    <Pagination className="mb-0" style={{ fontSize: '0.85rem' }}>
                      <Pagination.Prev 
                        disabled={pagination.page === 1}
                        onClick={() => handlePageChange(pagination.page - 1)}
                      />
                      
                      {[...Array(pagination.pages)].map((_, i) => {
                        const pageNum = i + 1;
                        if (
                          pageNum === 1 ||
                          pageNum === pagination.pages ||
                          (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
                        ) {
                          return (
                            <Pagination.Item
                              key={pageNum}
                              active={pageNum === pagination.page}
                              onClick={() => handlePageChange(pageNum)}
                              style={{ fontSize: '0.85rem' }}
                            >
                              {pageNum}
                            </Pagination.Item>
                          );
                        }
                        return null;
                      })}
                      
                      <Pagination.Next 
                        disabled={pagination.page === pagination.pages}
                        onClick={() => handlePageChange(pagination.page + 1)}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </Container>

      {/* View Modal - 85% width and smaller fonts */}
      <Modal 
        show={showViewModal} 
        onHide={() => setShowViewModal(false)}
        size="xl"
        centered
        dialogClassName="modal-85-width"
        scrollable
      >
        <Modal.Header closeButton className="text-white py-3" style={{backgroundColor: colors.TABLE_HEADERS}}>
          <Modal.Title className="d-flex align-items-center mb-0 w-100" style={{ fontSize: '1.25rem' }}>
            <div className="d-flex justify-content-between align-items-center w-100">
              <div>
                <FaEye className="me-2" />
                Booking Details - {selectedBooking?.name || 'Loading...'}
              </div>
              <div>
                {selectedBooking && getStatusBadge(selectedBooking.isUnread)}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className="p-0" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {modalLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3" style={{ fontSize: '0.9rem' }}>Loading booking details...</p>
            </div>
          ) : selectedBooking ? (
            <Container fluid className="p-3">
              {/* Top Summary Cards */}
              <Row className="mb-3 g-3">
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100 bg-primary bg-opacity-10">
                    <Card.Body className="d-flex align-items-center p-3">
                      <div className="bg-primary text-white rounded-circle p-2 me-3">
                        <FaUser size={18} />
                      </div>
                      <div>
                        <h6 className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>Customer</h6>
                        <h4 className="mb-0" style={{ fontSize: '1.1rem' }}>{selectedBooking.name}</h4>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100 bg-info bg-opacity-10">
                    <Card.Body className="d-flex align-items-center p-3">
                      <div className="bg-info text-white rounded-circle p-2 me-3">
                        <FaCalendarAlt size={18} />
                      </div>
                      <div>
                        <h6 className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>Event Date</h6>
                        <h4 className="mb-0" style={{ fontSize: '1.1rem' }}>{formatDate(selectedBooking.functionDate)}</h4>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100 bg-success bg-opacity-10">
                    <Card.Body className="d-flex align-items-center p-3">
                      <div className="bg-success text-white rounded-circle p-2 me-3">
                        <FaUserFriends size={18} />
                      </div>
                      <div>
                        <h6 className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>Total Guests</h6>
                        <h4 className="mb-0" style={{ fontSize: '1.1rem' }}>{selectedBooking.guestCount || 0}</h4>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
                
                <Col md={3}>
                  <Card className="border-0 shadow-sm h-100 bg-warning bg-opacity-10">
                    <Card.Body className="d-flex align-items-center p-3">
                      <div className="bg-warning text-white rounded-circle p-2 me-3">
                        <FaClock size={18} />
                      </div>
                      <div>
                        <h6 className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>Time Slot</h6>
                        <h4 className="mb-0" style={{ fontSize: '1.1rem' }}>{getTimeSlotLabel(selectedBooking.time)}</h4>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Main Content in 2 Columns */}
              <Row className="g-3">
                {/* Left Column - Personal & Contact Details */}
                <Col lg={6}>
                  {/* Personal Information Card */}
                  <Card className="mb-3 border-0 shadow-sm">
                    <Card.Header className="bg-light py-2">
                      <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: '1rem' }}>
                        <FaUser className="me-2 text-primary" />
                        Personal Information
                      </h5>
                    </Card.Header>
                    <Card.Body className="p-3">
                      <Row>
                        <Col md={6}>
                          <div className="mb-2">
                            <div className="d-flex align-items-center mb-1">
                              <FaIdCard className="me-2 text-muted" size={14} />
                              <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Full Name</strong>
                            </div>
                            <p className="fw-bold" style={{ fontSize: '0.9rem' }}>{selectedBooking.name}</p>
                          </div>
                          
                          <div className="mb-2">
                            <div className="d-flex align-items-center mb-1">
                              <FaEnvelope className="me-2 text-muted" size={14} />
                              <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Email Address</strong>
                            </div>
                            <p className="fw-bold" style={{ fontSize: '0.9rem' }}>{selectedBooking.email}</p>
                          </div>
                        </Col>
                        
                        <Col md={6}>
                          {/* Phone Number - Added prominently */}
                          <div className="mb-2">
                            <div className="d-flex align-items-center mb-1">
                              <FaPhone className="me-2 text-muted" size={14} />
                              <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Phone Number</strong>
                            </div>
                            <p className="fw-bold" style={{ fontSize: '0.9rem' }}>{selectedBooking.phoneNo || 'Not provided'}</p>
                          </div>
                          
                          {selectedBooking.cnic && (
                            <div className="mb-2">
                              <div className="d-flex align-items-center mb-1">
                                <FaIdCard className="me-2 text-muted" size={14} />
                                <strong className="text-muted" style={{ fontSize: '0.85rem' }}>CNIC Number</strong>
                              </div>
                              <p className="fw-bold" style={{ fontSize: '0.9rem' }}>{selectedBooking.cnic}</p>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>

                  {/* Event Details Card */}
                  <Card className="mb-3 border-0 shadow-sm">
                    <Card.Header className="bg-light py-2">
                      <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: '1rem' }}>
                        <FaCalendarDay className="me-2 text-success" />
                        Event Details
                      </h5>
                    </Card.Header>
                    <Card.Body className="p-3">
                      <Row>
                        <Col md={6}>
                          <div className="mb-2">
                            <div className="d-flex align-items-center mb-1">
                              <FaUserFriends className="me-2 text-muted" size={14} />
                              <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Guest Count</strong>
                            </div>
                            <Badge bg="primary" className="fs-6 px-2 py-1" style={{ fontSize: '0.85rem' }}>
                              {selectedBooking.guestCount || 0} Guests
                            </Badge>
                          </div>
                          
                          <div className="mb-2">
                            <div className="d-flex align-items-center mb-1">
                              <FaVenusMars className="me-2 text-muted" size={14} />
                              <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Time Slot</strong>
                            </div>
                            <Badge bg="info" className="fs-6 px-2 py-1" style={{ fontSize: '0.85rem' }}>
                              {getTimeSlotLabel(selectedBooking.time)}
                            </Badge>
                          </div>
                        </Col>
                        
                        <Col md={6}>
                          <div className="mb-2">
                            <div className="d-flex align-items-center mb-1">
                              <FaMapMarkerAlt className="me-2 text-muted" size={14} />
                              <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Venue</strong>
                            </div>
                            <p className="fw-bold" style={{ fontSize: '0.9rem' }}>{selectedBooking.venue || 'Not specified'}</p>
                          </div>
                          
                          <div className="mb-2">
                            <div className="d-flex align-items-center mb-1">
                              <FaTag className="me-2 text-muted" size={14} />
                              <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Event Type</strong>
                            </div>
                            <p className="fw-bold" style={{ fontSize: '0.9rem' }}>{selectedBooking.eventType || 'Not specified'}</p>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Right Column - Schedule, Menu & Additional Info */}
                <Col lg={6}>
                  {/* Schedule & Timeline Card */}
                  <Card className="mb-3 border-0 shadow-sm">
                    <Card.Header className="bg-light py-2">
                      <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: '1rem' }}>
                        <FaClock className="me-2 text-warning" />
                        Schedule & Timeline
                      </h5>
                    </Card.Header>
                    <Card.Body className="p-3">
                      <div className="timeline">
                        <div className="timeline-item">
                          <div className="timeline-marker bg-primary"></div>
                          <div className="timeline-content">
                            <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Booking Created</h6>
                            <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                              {formatDate(selectedBooking.createdAt)} at {formatTime(selectedBooking.createdAt)}
                            </p>
                          </div>
                        </div>
                        
                        <div className="timeline-item">
                          <div className="timeline-marker bg-success"></div>
                          <div className="timeline-content">
                            <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Event Scheduled</h6>
                            <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                              {formatDate(selectedBooking.functionDate)} at {selectedBooking.time || 'Not specified'}
                            </p>
                          </div>
                        </div>
                        
                        {selectedBooking.isUnread === false && (
                          <div className="timeline-item">
                            <div className="timeline-marker bg-info"></div>
                            <div className="timeline-content">
                              <h6 className="mb-1" style={{ fontSize: '0.9rem' }}>Marked as Viewed</h6>
                              <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                                {formatDate(selectedBooking.updatedAt || selectedBooking.createdAt)}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>

                  {/* Menu Details Card */}
                  {selectedBooking.menu && (
                    <Card className="mb-3 border-0 shadow-sm">
                      <Card.Header className="bg-light py-2">
                        <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: '1rem' }}>
                          <FaUtensils className="me-2 text-danger" />
                          Menu Details
                        </h5>
                      </Card.Header>
                      <Card.Body className="p-3">
                        <div className="row g-2">
                          {parseMenuData(selectedBooking.menu).map((menuSection, index) => (
                            <div key={index} className="col-md-6 mb-2">
                              <div className="card h-100">
                                <div className="card-header bg-light py-1">
                                  <h6 className="mb-0" style={{ fontSize: '0.85rem' }}>{menuSection.title}</h6>
                                </div>
                                <div className="card-body p-2">
                                  <div className="menu-items">
                                    {menuSection.items.split(',').map((item, itemIndex) => (
                                      <div key={itemIndex} className="d-flex align-items-center mb-1">
                                        <FaGlassCheers className="me-2 text-muted" size={12} />
                                        <span style={{ fontSize: '0.8rem' }}>{item.trim()}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Additional Information Card */}
                  <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-light py-2">
                      <h5 className="mb-0 d-flex align-items-center" style={{ fontSize: '1rem' }}>
                        <FaRegCommentDots className="me-2 text-secondary" />
                        Additional Information
                      </h5>
                    </Card.Header>
                    <Card.Body className="p-3">
                      <div className="mb-2">
                        <div className="d-flex align-items-center mb-1">
                          <FaStar className="me-2 text-muted" size={14} />
                          <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Special Requests</strong>
                        </div>
                        <p className="fw-bold" style={{ fontSize: '0.9rem' }}>{selectedBooking.specialRequests || 'No special requests'}</p>
                      </div>
                      
                      <div className="mb-2">
                        <div className="d-flex align-items-center mb-1">
                          <FaMoneyBill className="me-2 text-muted" size={14} />
                          <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Budget</strong>
                        </div>
                        <p className="fw-bold" style={{ fontSize: '0.9rem' }}>{selectedBooking.budget || 'Not specified'}</p>
                      </div>
                      
                      <div className="mb-2">
                        <div className="d-flex align-items-center mb-1">
                          <FaInfoCircle className="me-2 text-muted" size={14} />
                          <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Additional Notes</strong>
                        </div>
                        <p className="fw-bold" style={{ fontSize: '0.9rem' }}>{selectedBooking.notes || 'No additional notes'}</p>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Bottom Section - Booking Metadata */}
              <Row className="mt-3">
                <Col>
                  <Card className="border-0 shadow-sm bg-light">
                    <Card.Body className="p-3">
                      <Row>
                        <Col md={4}>
                          <div className="mb-2">
                            <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Booking ID:</strong>
                            <div className="font-monospace small" style={{ fontSize: '0.8rem' }}>{selectedBooking._id}</div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-2">
                            <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Created:</strong>
                            <div style={{ fontSize: '0.8rem' }}>{formatDate(selectedBooking.createdAt)} at {formatTime(selectedBooking.createdAt)}</div>
                          </div>
                        </Col>
                        <Col md={4}>
                          <div className="mb-2">
                            <strong className="text-muted" style={{ fontSize: '0.85rem' }}>Last Updated:</strong>
                            <div style={{ fontSize: '0.8rem' }}>{formatDate(selectedBooking.updatedAt || selectedBooking.createdAt)} at {formatTime(selectedBooking.updatedAt || selectedBooking.createdAt)}</div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          ) : (
            <div className="text-center py-5">
              <FaCalendarAlt size={48} className="text-muted mb-3" />
              <h4 style={{ fontSize: '1.25rem' }}>No booking selected</h4>
              <p className="text-muted" style={{ fontSize: '0.9rem' }}>Please select a booking to view details</p>
            </div>
          )}
        </Modal.Body>
        
        <Modal.Footer className="bg-light py-2">
          <div className="d-flex justify-content-between w-100">
            <div>
              <Button 
                variant="outline-secondary" 
                onClick={() => setShowViewModal(false)} 
                className="px-3 py-1 me-2"
                style={{ fontSize: '0.85rem' }}
              >
                Close
              </Button>
              <Button 
                variant="outline-danger" 
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedBooking(selectedBooking);
                  setShowDeleteModal(true);
                }}
                className="px-3 py-1"
                style={{ fontSize: '0.85rem' }}
              >
                <FaTrash className="me-2" size={12} />
                Delete Booking
              </Button>
            </div>
            
          </div>
        </Modal.Footer>
      </Modal>

      
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-danger" style={{ fontSize: '1.1rem' }}>
            <FaTrash className="me-2" />
            Delete Booking
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          {selectedBooking && (
            <>
              <Alert variant="danger" className="d-flex align-items-center py-2" style={{ fontSize: '0.85rem' }}>
                <FaInfoCircle className="me-2" size={16} />
                <strong>Warning:</strong> This action cannot be undone!
              </Alert>
              
              <p className="lead" style={{ fontSize: '0.9rem' }}>
                Are you sure you want to delete the booking for{' '}
                <strong className="text-primary">{selectedBooking.name || 'this booking'}</strong>?
              </p>
              
              <Card className="border-danger">
                <Card.Body className="p-3">
                  <Row>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong style={{ fontSize: '0.85rem' }}>Email:</strong>
                        <div style={{ fontSize: '0.85rem' }}>{selectedBooking.email || 'N/A'}</div>
                      </div>
                      <div className="mb-2">
                        <strong style={{ fontSize: '0.85rem' }}>Phone:</strong>
                        <div style={{ fontSize: '0.85rem' }}>{selectedBooking.phoneNo || 'N/A'}</div>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="mb-2">
                        <strong style={{ fontSize: '0.85rem' }}>Event Date:</strong>
                        <div style={{ fontSize: '0.85rem' }}>{formatDate(selectedBooking.functionDate)}</div>
                      </div>
                      <div className="mb-2">
                        <strong style={{ fontSize: '0.85rem' }}>Status:</strong>
                        <div>{getStatusBadge(selectedBooking.isUnread)}</div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </>
          )}
        </Modal.Body>
        
        <Modal.Footer className="py-2">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} className="px-3 py-1" style={{ fontSize: '0.85rem' }}>
            Cancel
          </Button>
          <Button 
            variant="danger"
            onClick={() => selectedBooking && handleDeleteBooking(selectedBooking._id)}
            className="px-3 py-1 d-flex align-items-center"
            style={{ fontSize: '0.85rem' }}
          >
            <FaTrash className="me-2" size={12} />
            Delete Permanently
          </Button>
        </Modal.Footer>
      </Modal>

      {/* CSS Styling */}
      <style>
        {`
          .modal-85-width {
            max-width: 85%;
            width: 85%;
            margin: auto;
          }
          
          @media (max-width: 1200px) {
            .modal-85-width {
              max-width: 90%;
              width: 90%;
            }
          }
          
          @media (max-width: 768px) {
            .modal-85-width {
              max-width: 95%;
              width: 95%;
              margin: 10px;
            }
          }
          
          .timeline {
            position: relative;
            padding-left: 25px;
          }
          
          .timeline::before {
            content: '';
            position: absolute;
            left: 12px;
            top: 0;
            bottom: 0;
            width: 2px;
            background-color: #e9ecef;
          }
          
          .timeline-item {
            position: relative;
            margin-bottom: 15px;
          }
          
          .timeline-marker {
            position: absolute;
            left: -25px;
            top: 5px;
            width: 10px;
            height: 10px;
            border-radius: 50%;
          }
          
          .timeline-content {
            padding-left: 8px;
          }
          
          .menu-items {
            max-height: 120px;
            overflow-y: auto;
          }
        `}
      </style>
    </Layout>
  );
};

export default Bookings;